import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { abbrNumber } from '@hotjar/insights-common/lib/number';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';

interface UsePlanAllowances {
  getPlanAllowances: (plan?: Plan | undefined | null) => {
    sampleRate: number;
    ccDailyAllowance: number;
    hasContinuousCapture: boolean;
    allowanceLabel: string;
  };
}

interface UseProductAllowances {
  getAllowances: (
    product?: AnalyticsProduct | FeedbackProduct | undefined | null,
    coveragePercentage?: number,
  ) => {
    allowanceLabel: string;
    ccDailyAllowance?: number;
    monthlyResponses?: number;
    trafficCoverageLabel: string;
  };
}

export function usePlanAllowances(): UsePlanAllowances {
  const { t } = useTranslation('revenue');

  return useMemo(
    () => ({
      getPlanAllowances: (plan) => {
        if (!plan) {
          return {
            sampleRate: 0,
            ccDailyAllowance: 0,
            hasContinuousCapture: false,
            allowanceLabel: '',
          };
        }

        return {
          sampleRate: plan.sampleRate,
          ccDailyAllowance: plan.ccDailyAllowance,
          hasContinuousCapture: plan.ccDailyAllowance > 0,
          allowanceLabel:
            plan.ccDailyAllowance === 0
              ? t('pageviews-per-day', { sampleRate: abbrNumber(plan.sampleRate) })
              : t('sessions-per-day', { ccDailyAllowance: abbrNumber(plan.ccDailyAllowance) }),
        };
      },
    }),
    [t],
  );
}

export function isAnalyticsProduct(
  maybeIsAnalyticsProduct: AnalyticsProduct | FeedbackProduct | null | undefined,
): maybeIsAnalyticsProduct is AnalyticsProduct {
  return maybeIsAnalyticsProduct?.type === ProductType.Analytics;
}

export function isFeedbackProduct(
  maybeIsFeedbackProduct: AnalyticsProduct | FeedbackProduct | null | undefined,
): maybeIsFeedbackProduct is AnalyticsProduct {
  return maybeIsFeedbackProduct?.type === ProductType.Feedback;
}

export function useProductAllowances(): UseProductAllowances {
  const { t } = useTranslation('revenue');

  return useMemo(
    () => ({
      getAllowances: (product, coveragePercentage) => {
        if (!product) {
          return {
            ccDailyAllowance: 0,
            allowanceLabel: '',
            trafficCoverageLabel: '',
            monthlyResponses: 0,
          };
        }
        if (isAnalyticsProduct(product)) {
          return {
            ccDailyAllowance: product.dailyCcSessions,
            allowanceLabel: t('sessions-per-day', {
              ccDailyAllowance: abbrNumber(product.dailyCcSessions),
            }),
            trafficCoverageLabel: t('traffic-coverage', {
              trafficCoverage: coveragePercentage,
            }),
          };
        }
        return {
          monthlyResponses: product.monthlyResponses,
          allowanceLabel: t('responses-per-month', {
            responses: abbrNumber(product.monthlyResponses),
          }),
          trafficCoverageLabel: t('traffic-coverage', {
            trafficCoverage: coveragePercentage,
          }),
        };
      },
    }),
    [t],
  );
}
